import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Menu, 
  X, 
  PenTool, 
  TreeDeciduous,
  Stethoscope,
  MessageCircle,
  Workflow
} from 'lucide-react';
import { useBrand } from '../context/BrandContext';

/**
 * US Citrus Nursery Landing Page
 * Why this matters: Creates a professional homepage showcasing actual tools from Navigation.tsx
 * using US Citrus Nursery's green/orange brand colors
 */
const LandingPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { brand, getPath } = useBrand();

  // Active brand colors
  const colors = {
    primaryGreen: brand.theme.primary,
    secondaryGreen: brand.theme.secondary,
    accentOrange: brand.theme.accent,
    warmOrange: brand.theme.accent,
    backgroundTan: brand.theme.background,
    backgroundGray: '#FAFAF8',
    textDark: brand.theme.text,
    textMuted: '#5a5a5a',
    white: '#ffffff',
  };

  /**
   * Handle body scroll lock when mobile menu is open
   */
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    } else {
      setIsMobileMenuOpen(true);
      setIsClosing(false);
    }
  };

  const closeMobileMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  // Featured tools - main tools from Navigation.tsx
  const featuredTools = [
    {
      icon: <Workflow size={24} />,
      title: 'Create Citrus Tree Articles',
      description: 'Generate new articles about citrus varieties and care guides.',
      link: getPath('create-tree-articles')
    },
    {
      icon: <PenTool size={24} />,
      title: 'Rewrite Old Blog Posts',
      description: 'Update existing blog posts with fresh content and SEO improvements.',
      link: getPath('rewrite-blog-posts')
    },
    {
      icon: <MessageCircle size={24} />,
      title: 'People Also Ask',
      description: 'Create FAQ content based on real search queries.',
      link: getPath('people-also-ask-agent')
    },
    {
      icon: <TreeDeciduous size={24} />,
      title: 'Citrus Quiz',
      description: 'Customer tool to find the right tree for their needs.',
      link: getPath('quiz'),
      external: true
    },
    {
      icon: <Stethoscope size={24} />,
      title: 'Citrus Doctor',
      description: 'Customer tool to diagnose tree health issues.',
      link: getPath('citrus-doctor'),
      external: true
    },
  ];

  // Blog content tools
  const blogTools = [
    {
      icon: <Workflow size={24} />,
      title: 'Create Citrus Tree Articles',
      description: 'Generate articles for lemon, orange, lime, and other varieties.',
      link: getPath('create-tree-articles')
    },
    {
      icon: <PenTool size={24} />,
      title: 'Rewrite Old Blog Posts',
      description: 'Paste an old blog URL and get an updated version.',
      link: getPath('rewrite-blog-posts')
    },
    {
      icon: <MessageCircle size={24} />,
      title: 'People Also Ask',
      description: 'Generate FAQ content from Google search queries.',
      link: getPath('people-also-ask-agent')
    },
  ];

  // Customer-facing tools
  const customerTools = [
    {
      icon: <TreeDeciduous size={24} />,
      title: 'Citrus Quiz',
      description: 'Helps customers pick the right tree based on their climate and space.',
      link: getPath('quiz'),
      external: true
    },
    {
      icon: <Stethoscope size={24} />,
      title: 'Citrus Doctor',
      description: 'Helps customers diagnose yellow leaves, pests, and diseases.',
      link: getPath('citrus-doctor'),
      external: true
    },
  ];


  // Render tool link (handles both internal and external links)
  const renderToolLink = (tool: typeof featuredTools[0], index: number) => {
    const linkContent = (
      <>
        {/* Icon */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '0.5rem',
          border: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textDark,
        }}>
          {tool.icon}
        </div>

        {/* Content */}
        <div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: colors.textDark,
            marginBottom: '0.5rem',
          }}>
            {tool.title}
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: colors.textMuted,
            lineHeight: '1.5',
            margin: 0,
          }}>
            {tool.description}
          </p>
        </div>

        {/* Get Started Link */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          color: colors.textDark,
          fontSize: '0.875rem',
          fontWeight: '500',
          marginTop: 'auto',
        }}>
          Get Started <ArrowRight size={16} />
        </div>
      </>
    );

    const commonStyles: React.CSSProperties = {
      backgroundColor: colors.white,
      border: '1px solid #e8e8e8',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    };

    if (tool.external) {
      return (
        <a
          key={index}
          href={tool.link}
          target="_blank"
          rel="noopener noreferrer"
          style={commonStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = colors.secondaryGreen;
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#e8e8e8';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {linkContent}
        </a>
      );
    }

    return (
      <Link
        key={index}
        to={tool.link}
        style={commonStyles}
        onMouseOver={(e) => {
          e.currentTarget.style.borderColor = colors.secondaryGreen;
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.borderColor = '#e8e8e8';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {linkContent}
      </Link>
    );
  };

  // Render simple tool card
  const renderSimpleToolCard = (tool: typeof blogTools[0], index: number) => {
    const linkContent = (
      <>
        {/* Icon */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '0.5rem',
          border: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textDark,
        }}>
          {tool.icon}
        </div>

        {/* Content */}
        <div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: colors.textDark,
            marginBottom: '0.5rem',
          }}>
            {tool.title}
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: colors.textMuted,
            lineHeight: '1.5',
            margin: 0,
          }}>
            {tool.description}
          </p>
        </div>

        {/* Arrow Link */}
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textDark,
          marginTop: 'auto',
        }}>
          <ArrowRight size={16} />
        </div>
      </>
    );

    const commonStyles: React.CSSProperties = {
      backgroundColor: colors.white,
      border: '1px solid #e8e8e8',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    };

    if ('external' in tool && tool.external) {
      return (
        <a
          key={index}
          href={tool.link}
          target="_blank"
          rel="noopener noreferrer"
          style={commonStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = colors.secondaryGreen;
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#e8e8e8';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {linkContent}
        </a>
      );
    }

    return (
      <Link
        key={index}
        to={tool.link}
        style={commonStyles}
        onMouseOver={(e) => {
          e.currentTarget.style.borderColor = colors.secondaryGreen;
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.borderColor = '#e8e8e8';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {linkContent}
      </Link>
    );
  };

  return (
    <div className="landing-page" style={{ backgroundColor: colors.white }}>
      {/* Header - White background to blend with logo */}
      <header style={{ 
        backgroundColor: colors.white, 
        borderBottom: `1px solid #e8e8e8`,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div className="header-container" style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="header-left">
            <Link to={getPath()} className="us-citrus-logo-header">
              {brand.logoSrc ? (
                <img
                  src={brand.logoSrc}
                  alt={`${brand.displayName} Logo`}
                  style={{ height: '3.5rem', width: 'auto' }}
                />
              ) : (
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.textDark }}>
                  {brand.domain}
                </span>
              )}
            </Link>
          </div>
          
          {/* Header Actions */}
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Link 
              to={getPath('create-tree-articles')}
              className="desktop-only"
              style={{
                padding: '0.625rem 1.5rem',
                backgroundColor: colors.primaryGreen,
                color: colors.white,
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                border: `2px solid ${colors.primaryGreen}`,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#1e3a0f';
                e.currentTarget.style.borderColor = '#1e3a0f';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryGreen;
                e.currentTarget.style.borderColor = colors.primaryGreen;
              }}
            >
              Go to App
            </Link>
            
            {/* Mobile Hamburger Menu Button */}
            <button 
              className="mobile-menu-toggle mobile-only"
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: colors.textDark
              }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className={`mobile-menu-overlay mobile-only ${isClosing ? 'closing' : ''}`} 
          onClick={closeMobileMenu}
        >
          <div 
            className={`mobile-menu-content ${isClosing ? 'closing' : ''}`} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="landing-mobile-menu">
              <div className="nav-section">
                <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                  <Link 
                    to={getPath('create-tree-articles')}
                    onClick={closeMobileMenu}
                    style={{
                      display: 'inline-block',
                      padding: '0.75rem 2rem',
                      backgroundColor: colors.primaryGreen,
                      color: colors.white,
                      textDecoration: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      border: `2px solid ${colors.primaryGreen}`,
                      minWidth: '12.5rem',
                    }}
                  >
                    Go to App
                </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section with gradient background */}
      <section style={{
        background: `linear-gradient(180deg, ${colors.backgroundTan} 0%, ${colors.white} 100%)`,
        padding: '4rem 1.5rem 6rem',
        textAlign: 'center',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Main Headline */}
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            lineHeight: '1.1',
            color: colors.textDark,
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em',
          }}>
            {brand.displayName} AI Tools
          </h1>
          
          {/* Subtitle */}
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
            lineHeight: '1.6',
            color: colors.textMuted,
            marginBottom: '2.5rem',
            maxWidth: '700px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            Create branded content, run customer quizzes, and manage AI workflows for {brand.domain}.
          </p>
          
          {/* CTA Button */}
            <Link 
            to={getPath('create-tree-articles')}
              style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2.5rem',
              backgroundColor: colors.secondaryGreen,
              color: colors.white,
              textDecoration: 'none',
              borderRadius: '0.75rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#3d9140';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = colors.secondaryGreen;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Explore Tools <ArrowRight size={20} />
            </Link>
        </div>
      </section>

      {/* Featured Tools Section */}
      <section style={{
        padding: '5rem 1.5rem',
        backgroundColor: colors.white,
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '700',
              color: colors.textDark,
              marginBottom: '0.5rem',
            }}>
              Featured Tools
            </h2>
            <div style={{
              width: '100%',
              height: '1px',
              background: `linear-gradient(90deg, transparent, #e0e0e0, transparent)`,
              margin: '1rem auto 2rem',
            }} />
            <p style={{
              fontSize: '1.1rem',
              color: colors.textMuted,
              marginBottom: '3rem',
            }}>
              Quick access to your most-used tools
            </p>
          </div>
          
          {/* Tools Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
          }}>
            {featuredTools.map((tool, index) => renderToolLink(tool, index))}
          </div>
        </div>
      </section>

      {/* Blog Agents Section */}
      <section style={{
        padding: '5rem 1.5rem',
        backgroundColor: colors.white,
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '700',
              color: colors.textDark,
              marginBottom: '0.5rem',
            }}>
              Blog Agents
            </h2>
            <div style={{
              width: '100%',
              height: '1px',
              background: `linear-gradient(90deg, transparent, #e0e0e0, transparent)`,
              margin: '1rem auto 2rem',
            }} />
            <p style={{
              fontSize: '1.1rem',
              color: colors.textMuted,
              marginBottom: '3rem',
            }}>
              Write and update citrus content
            </p>
            </div>

          {/* Blog Tools Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
          }}>
            {blogTools.map((tool, index) => renderSimpleToolCard(tool, index))}
          </div>
        </div>
      </section>

      {/* Customer Tools Section */}
      <section style={{
        padding: '5rem 1.5rem',
        backgroundColor: colors.backgroundGray,
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '700',
              color: colors.textDark,
              marginBottom: '0.5rem',
            }}>
              Customer-Facing Tools
            </h2>
            <div style={{
              width: '100%',
              height: '1px',
              background: `linear-gradient(90deg, transparent, #e0e0e0, transparent)`,
              margin: '1rem auto 2rem',
            }} />
            <p style={{
              fontSize: '1.1rem',
              color: colors.textMuted,
              marginBottom: '3rem',
            }}>
              Embed these on your website for customers
            </p>
      </div>

          {/* Customer Tools Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
          }}>
            {customerTools.map((tool, index) => renderSimpleToolCard(tool as typeof blogTools[0], index))}
           </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2rem 1.5rem',
        backgroundColor: colors.white,
        borderTop: '1px solid #e8e8e8',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '0.875rem',
          color: colors.textMuted,
          margin: 0,
        }}>
          © {new Date().getFullYear()} {brand.displayName}. All rights reserved.
        </p>
      </footer>

      {/* Responsive styles */}
      <style>{`
        .desktop-only {
          display: inline-flex;
        }
        .mobile-only {
          display: none;
        }
        @media (max-width: 768px) {
          .desktop-only {
            display: none !important;
          }
          .mobile-only {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage; 
